import { useContext, useEffect } from "react";
import { ApiContext, useApi } from "../apiProvider";
import { ApiManagerBase, DefaultAdapterBase } from "../AdapterRule";
import { ApiVersionName, ResultObj, allFetch } from "../Domain";
import { TypeYN } from "../types";

import { DateTimeFormate, defaultDateTimeToDotNet6Format, defaultDateToDotNet6Format, dotNet6DateTimeToDefaulFormat, normalizeDsId } from "../function";

/** 使用報表1 API的hook，封裝EC訂單、庫存、調撥等相關操作。*/
export const useReportApi = () => {
    const apiManagerName = "ReportApiManager";
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error("useReportApi must be used within an ApiProvider");
    }

    let { apiManagers, registerApiManager } = context;
    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newReportApiManager = new ReportApiManager();
            newReportApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("Error initializing Report API Manager", e);
            });
        }
    }, [apiManagers[apiManagerName]]);

    return useApi<ReportApiManager>(apiManagerName);
};

/** 報表1 API管理器，提供EC訂單、庫存、調撥等相關報表操作。*/
export class ReportApiManager extends ApiManagerBase<ReportDefaultAdapter> implements ReportApiInterface {
    apiManagerVersion: Record<ApiVersionName | "defaultAdapter", ReportDefaultAdapter> = {
        [ApiVersionName.DOT_NET_6]: new ReportDotNet6Adapter(),
        [ApiVersionName.DOT_NET_6_AM_N]: new ReportDotNet6Adapter(),
        [ApiVersionName.FRAMEWORK]: new ReportFrameworkAdapter(),
        defaultAdapter: new ReportDefaultAdapter(),
    }
    adapter = this.apiManagerVersion.defaultAdapter;

    WorkClockRecordStatistic = this.checkDefaultHOF(async (obj: WorkClockRecordStatisticParams) => await this.adapter.WorkClockRecordStatistic(obj));
    AttendanceStatistic = this.checkDefaultHOF(async (obj: AttendanceStatisticParams) => await this.adapter.AttendanceStatistic(obj));
    ScheduleXEmployeeReport = this.checkDefaultHOF(async (obj: ScheduleXEmployeeReportParams) => await this.adapter.ScheduleXEmployeeReport(obj));
    LeaveRecordReport = this.checkDefaultHOF(async (obj: LeaveRecordReportParams) => await this.adapter.LeaveRecordReport(obj));
    LeaveRemainAnnualReport = this.checkDefaultHOF(async (obj: LeaveRemainAnnualReportParams) => await this.adapter.LeaveRemainAnnualReport(obj));
    DispatchCompanyReport = this.checkDefaultHOF(async (obj: DispatchCompanyReportParams) => await this.adapter.DispatchCompanyReport(obj));
    CountTestDriveByWeek = this.checkDefaultHOF(async (obj: CountTestDriveByWeekParams) => await this.adapter.CountTestDriveByWeek(obj));
    TechnicianPerformanceReport = this.checkDefaultHOF(async (obj: TechnicianPerformanceReportParams) => await this.adapter.TechnicianPerformanceReport(obj));
    TechnicianPerformanceDetailReport = this.checkDefaultHOF(async (obj: TechnicianPerformanceDetailReportParams) => await this.adapter.TechnicianPerformanceDetailReport(obj));
    IncomeClassificationReport = this.checkDefaultHOF(async (obj: IncomeClassificationReportParams) => await this.adapter.IncomeClassificationReport(obj));
    WIPIncomeClassificationReport = this.checkDefaultHOF(async (obj: WIPIncomeClassificationReportParams) => await this.adapter.WIPIncomeClassificationReport(obj));
    WIPIncomeClassificationDetail = this.checkDefaultHOF(async (obj: WIPIncomeClassificationDetailParams) => await this.adapter.WIPIncomeClassificationDetail(obj));
    InTimeInventoryReport = this.checkDefaultHOF(async (obj: InTimeInventoryReportParams) => await this.adapter.InTimeInventoryReport(obj));
    RepairListDetailReport = this.checkDefaultHOF(async (obj: RepairListDetailReportParams) => await this.adapter.RepairListDetailReport(obj));
    WorkOrderPayLogReport = this.checkDefaultHOF(async (obj: WorkOrderPayLogReportParams) => await this.adapter.WorkOrderPayLogReport(obj));
    UserReport = this.checkDefaultHOF(async (obj: UserReportParams) => await this.adapter.UserReport(obj));
    UserGenderDashboard = this.checkDefaultHOF(async (obj: UserGenderDashboardParams) => await this.adapter.UserGenderDashboard(obj));
    UserResignDashboard = this.checkDefaultHOF(async (obj: UserResignDashboardParams) => await this.adapter.UserResignDashboard(obj));
    CourseDashboard = this.checkDefaultHOF(async (obj: CourseDashboardParams) => await this.adapter.CourseDashboard(obj));
    CourseAttendRateDashboard = this.checkDefaultHOF(async (obj: CourseRateReportParams) => await this.adapter.CourseAttendRateDashboard(obj));
    CourseAnswerRateDashboard = this.checkDefaultHOF(async (obj: CourseRateReportParams) => await this.adapter.CourseAnswerRateDashboard(obj));
    CoursePretestAnswerRateDashboard = this.checkDefaultHOF(async (obj: CourseRateReportParams) => await this.adapter.CoursePretestAnswerRateDashboard(obj));
    CourseCompleteRateDashboard = this.checkDefaultHOF(async (obj: CourseRateReportParams) => await this.adapter.CourseCompleteRateDashboard(obj));
    StockUsageRankingReport = this.checkDefaultHOF(async (obj: StockUsageRankingReportParams) => await this.adapter.StockUsageRankingReport(obj));
    EC_OrderSaleSummary = this.checkDefaultHOF(async (obj: EC_OrderSaleSummaryParams) => await this.adapter.EC_OrderSaleSummary(obj));
    EC_OrderPromotionSummary = this.checkDefaultHOF(async (obj: EC_OrderPromotionSummaryParams) => await this.adapter.EC_OrderPromotionSummary(obj));
    EC_CustomerSummary = this.checkDefaultHOF(async (obj: EC_CustomerSummaryParams) => await this.adapter.EC_CustomerSummary(obj));
    SafeStorageAlert = this.checkDefaultHOF(async (obj: SafeStorageAlertParams) => await this.adapter.SafeStorageAlert(obj));
    EC_StockSluggishSale = this.checkDefaultHOF(async (obj: EC_StockSluggishSaleParams) => await this.adapter.EC_StockSluggishSale(obj));
    EC_OrderRevenueDetail = this.checkDefaultHOF(async (obj: EC_OrderRevenueDetailParams) => await this.adapter.EC_OrderRevenueDetail(obj));
    EC_OrderInvoiceDetail = this.checkDefaultHOF(async (obj: EC_OrderInvoiceDetailParams) => await this.adapter.EC_OrderInvoiceDetail(obj));
    EC_OrderSaleDetail = this.checkDefaultHOF(async (obj: EC_OrderSaleDetailParams) => await this.adapter.EC_OrderSaleDetail(obj));
    StockTransferDetailReport = this.checkDefaultHOF(async (obj: StockTransferDetailReportParams) => await this.adapter.StockTransferDetailReport(obj));
    RollCallStatistic = this.checkDefaultHOF(async (obj: RollCallStatisticParams) => await this.adapter.RollCallStatistic(obj));
    UserRollCallStatistic = this.checkDefaultHOF(async (obj: UserRollCallStatisticParams) => await this.adapter.UserRollCallStatistic(obj));
}

/** 報表1 API接口，定義所有相關報表操作。*/
interface ReportApiInterface {
    WorkClockRecordStatistic: (obj: WorkClockRecordStatisticParams) => Promise<ResultObj<WorkClockRecordStatisticResItem[]>>;
    AttendanceStatistic: (obj: AttendanceStatisticParams) => Promise<ResultObj<AttendanceStatisticResItem[]>>;
    ScheduleXEmployeeReport: (obj: ScheduleXEmployeeReportParams) => Promise<ResultObj<ScheduleXEmployeeReportResItem[]>>;
    LeaveRecordReport: (obj: LeaveRecordReportParams) => Promise<ResultObj<LeaveRecordReportResItem[]>>;
    LeaveRemainAnnualReport: (obj: LeaveRemainAnnualReportParams) => Promise<ResultObj<LeaveRemainAnnualReportResItem[]>>;
    TechnicianPerformanceDetailReport: (obj: TechnicianPerformanceDetailReportParams) => Promise<ResultObj>;
    IncomeClassificationReport: (obj: IncomeClassificationReportParams) => Promise<ResultObj>;
    WIPIncomeClassificationReport: (obj: WIPIncomeClassificationReportParams) => Promise<ResultObj<WIPIncomeClassificationReportResItem[]>>;
    WIPIncomeClassificationDetail: (obj: WIPIncomeClassificationDetailParams) => Promise<ResultObj<WIPIncomeClassificationDetailResItem[]>>;
    InTimeInventoryReport: (obj: InTimeInventoryReportParams) => Promise<ResultObj>;
    RepairListDetailReport: (obj: RepairListDetailReportParams) => Promise<ResultObj>;
    WorkOrderPayLogReport: (obj: WorkOrderPayLogReportParams) => Promise<ResultObj>;
    UserReport: (obj: UserReportParams) => Promise<ResultObj<UserReportResItem[]>>;
    CourseDashboard: (obj: CourseDashboardParams) => Promise<ResultObj<CourseDashboardRes>>;
    DispatchCompanyReport: (obj: DispatchCompanyReportParams) => Promise<ResultObj<DispatchCompanyReportResItem[]>>
    CountTestDriveByWeek: (obj: CountTestDriveByWeekParams) => Promise<ResultObj>
    TechnicianPerformanceReport: (obj: TechnicianPerformanceReportParams) => Promise<ResultObj<TechnicianPerformanceReportResItem[]>>
    UserGenderDashboard: (obj: UserGenderDashboardParams) => Promise<ResultObj<UserGenderDashboardRes[]>>;
    UserResignDashboard: (obj: UserResignDashboardParams) => Promise<ResultObj<UserResignDashboardRes[]>>;
    CourseAttendRateDashboard: (obj: CourseRateReportParams) => Promise<ResultObj<CourseAttendRateDashboardRes[]>>;
    CourseAnswerRateDashboard: (obj: CourseRateReportParams) => Promise<ResultObj<CourseAnswerRateDashboard[]>>;
    CoursePretestAnswerRateDashboard: (obj: CourseRateReportParams) => Promise<ResultObj<CourseAnswerRateDashboard[]>>;
    CourseCompleteRateDashboard: (obj: CourseRateReportParams) => Promise<ResultObj<CourseCompleteRateDashboard[]>>;
    StockUsageRankingReport: (obj: StockUsageRankingReportParams) => Promise<ResultObj<StockUsageRankingReportResItem[]>>;
    EC_OrderSaleSummary: (obj: EC_OrderSaleSummaryParams) => Promise<ResultObj<EC_OrderSaleSummaryRes>>;
    EC_OrderPromotionSummary: (obj: EC_OrderPromotionSummaryParams) => Promise<ResultObj<EC_OrderPromotionSummaryRes>>;
    EC_CustomerSummary: (obj: EC_CustomerSummaryParams) => Promise<ResultObj<EC_CustomerSummaryRes>>;
    SafeStorageAlert: (obj: SafeStorageAlertParams) => Promise<ResultObj<SafeStorageAlertRes>>;
    EC_StockSluggishSale: (obj: EC_StockSluggishSaleParams) => Promise<ResultObj<EC_StockSluggishSaleRes>>;
    EC_OrderRevenueDetail: (obj: EC_OrderRevenueDetailParams) => Promise<ResultObj<EC_OrderRevenueDetailResItem[]>>;
    EC_OrderInvoiceDetail: (obj: EC_OrderInvoiceDetailParams) => Promise<ResultObj<EC_OrderInvoiceDetailResItem[]>>;
    EC_OrderSaleDetail: (obj: EC_OrderSaleDetailParams) => Promise<ResultObj<EC_OrderSaleDetailResItem[]>>;
    StockTransferDetailReport: (obj: StockTransferDetailReportParams) => Promise<ResultObj<StockTransferDetailReportResItem[]>>;
    RollCallStatistic: (obj: RollCallStatisticParams) => Promise<ResultObj<RollCallStatisticResItem[]>>;
    UserRollCallStatistic: (obj: UserRollCallStatisticParams) => Promise<ResultObj<UserRollCallStatisticResItem[]>>;
}

class ReportDefaultAdapter extends DefaultAdapterBase implements ReportApiInterface {
    WorkClockRecordStatistic = async (obj: WorkClockRecordStatisticParams): Promise<ResultObj<WorkClockRecordStatisticResItem[]>> => await allFetch("Report/WorkClockRecordStatistic", {
        ...obj,
        date_start: defaultDateToDotNet6Format(obj.date_start),
        date_end: defaultDateToDotNet6Format(obj.date_end),
    }).then(res => ({
        ...res,
        data: res.data?.map((item: WorkClockRecordStatisticResItem) => ({
            ...item,
            time_to: dotNet6DateTimeToDefaulFormat(item.time_to),
            time_off: dotNet6DateTimeToDefaulFormat(item.time_off),
        }))
    }));
    AttendanceStatistic = async (obj: AttendanceStatisticParams): Promise<ResultObj<AttendanceStatisticResItem[]>> => await allFetch("Report/AttendanceStatistic", obj);
    ScheduleXEmployeeReport = async (obj: ScheduleXEmployeeReportParams): Promise<ResultObj<ScheduleXEmployeeReportResItem[]>> => await allFetch("Report/ScheduleXEmployeeReport", obj);
    LeaveRecordReport = async (obj: LeaveRecordReportParams): Promise<ResultObj<LeaveRecordReportResItem[]>> => await allFetch("Report/LeaveRecordReport", obj);
    LeaveRemainAnnualReport = async (obj: LeaveRemainAnnualReportParams): Promise<ResultObj<LeaveRemainAnnualReportResItem[]>> => await allFetch("Report/LeaveRemainAnnualReport", obj);
    DispatchCompanyReport = async (obj: DispatchCompanyReportParams): Promise<ResultObj<DispatchCompanyReportResItem[]>> => await allFetch("Report/DispatchCompanyReport", {
        ...obj,
        enter_date_start: defaultDateTimeToDotNet6Format(obj.enter_date_start),
        enter_date_end: defaultDateTimeToDotNet6Format(obj.enter_date_end),
        caseclose_date_start: defaultDateTimeToDotNet6Format(obj.caseclose_date_start),
        caseclose_date_end: defaultDateTimeToDotNet6Format(obj.caseclose_date_end),
    })
    CountTestDriveByWeek = async (obj: CountTestDriveByWeekParams): Promise<ResultObj> => await allFetch("Report/CountTestDriveByWeek", obj);
    TechnicianPerformanceReport = async (obj: TechnicianPerformanceReportParams): Promise<ResultObj<TechnicianPerformanceReportResItem[]>> => await allFetch("Report/TechnicianPerformanceReport", {
        ...obj,
        caseclose_date_end: obj.caseclose_date_end ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.caseclose_date_end, joinDate: "-", joinTime: ":"
            })) : null,
        caseclose_date_start: obj.caseclose_date_start ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.caseclose_date_start, joinDate: "-", joinTime: ":"
            })) : null,
        end_date: obj.end_date ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.end_date, joinDate: "-", joinTime: ":"
            })) : null,
        start_date: obj.start_date ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.start_date, joinDate: "-", joinTime: ":"
            })) : null,
    });
    TechnicianPerformanceDetailReport = async (obj: TechnicianPerformanceDetailReportParams): Promise<ResultObj<TechnicianPerformanceDetailReportResItem[]>> => await allFetch("Report/TechnicianPerformanceDetailReport", {
        ...obj,
        caseclose_date_end: obj.caseclose_date_end ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.caseclose_date_end, joinDate: "-", joinTime: ":"
            })) : null,
        caseclose_date_start: obj.caseclose_date_start ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.caseclose_date_start, joinDate: "-", joinTime: ":"
            })) : null,
        end_date: obj.end_date ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.end_date, joinDate: "-", joinTime: ":"
            })) : null,
        start_date: obj.start_date ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.start_date, joinDate: "-", joinTime: ":"
            })) : null,
    });
    IncomeClassificationReport = async (obj: IncomeClassificationReportParams): Promise<ResultObj> => await allFetch("Report/IncomeClassificationReport", {
        ...obj,
        start_date: defaultDateTimeToDotNet6Format(obj.start_date),
        end_date: defaultDateTimeToDotNet6Format(obj.end_date),
    })
    WIPIncomeClassificationReport = async (obj: WIPIncomeClassificationReportParams): Promise<ResultObj<WIPIncomeClassificationReportResItem[]>> => await allFetch("Report/WIPIncomeClassificationReport", obj);
    WIPIncomeClassificationDetail = async (obj: WIPIncomeClassificationDetailParams): Promise<ResultObj<WIPIncomeClassificationDetailResItem[]>> => await allFetch("Report/WIPIncomeClassificationDetail", obj);
    InTimeInventoryReport = async (obj: InTimeInventoryReportParams): Promise<ResultObj> => await allFetch("Report/InTimeInventoryReport", obj);
    RepairListDetailReport = async (obj: RepairListDetailReportParams): Promise<ResultObj> => await allFetch("Report/RepairListDetailReport", obj);
    WorkOrderPayLogReport = async (obj: WorkOrderPayLogReportParams): Promise<ResultObj> => await allFetch("Report/WorkOrderPayLogReport", {
        ...obj,
        time_enter_start: defaultDateTimeToDotNet6Format(obj.time_enter_start),
        time_enter_end: defaultDateTimeToDotNet6Format(obj.time_enter_end),
        time_leave_start: defaultDateTimeToDotNet6Format(obj.time_leave_start),
        time_leave_end: defaultDateTimeToDotNet6Format(obj.time_leave_end),
        complete_time_start: defaultDateTimeToDotNet6Format(obj.complete_time_start),
        complete_time_end: defaultDateTimeToDotNet6Format(obj.complete_time_end),
        caseclose_time_start: defaultDateTimeToDotNet6Format(obj.caseclose_time_start),
        caseclose_time_end: defaultDateTimeToDotNet6Format(obj.caseclose_time_end),
        invalid_time_start: defaultDateTimeToDotNet6Format(obj.invalid_time_start),
        invalid_time_end: defaultDateTimeToDotNet6Format(obj.invalid_time_end),
    });

    UserReport = async (obj: UserReportParams): Promise<ResultObj<UserReportResItem[]>> => await allFetch("Report/UserReport", obj);
    CourseDashboard = async (obj: CourseDashboardParams): Promise<ResultObj<CourseDashboardRes>> => await allFetch("Report/CourseDashboard", {
        ...obj,
        ds_ids: obj.ds_ids || undefined,
        course_date_start: obj.course_date_start ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_start, joinDate: "-", joinTime: ":"
            })) : null,
        course_date_end: obj.course_date_end ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_end, joinDate: "-", joinTime: ":"
            })) : null,
    });
    UserGenderDashboard = async (obj: UserGenderDashboardParams): Promise<ResultObj<UserGenderDashboardRes[]>> => await allFetch("Report/UserGenderDashboard", {
        ...obj,
        ds_ids: obj.ds_ids || undefined,
        on_board_date_start: obj.on_board_date_start ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.on_board_date_start, joinDate: "-", joinTime: ":"
            })) : null,
        on_board_date_end: obj.on_board_date_end ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.on_board_date_end, joinDate: "-", joinTime: ":"
            })) : null,
    });
    UserResignDashboard = async (obj: UserResignDashboardParams): Promise<ResultObj<UserResignDashboardRes[]>> => await allFetch("Report/UserResignDashboard", {
        ...obj,
        ds_ids: obj.ds_ids || undefined,
        date_start: obj.date_start ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.date_start, joinDate: "-", joinTime: ":"
            })) : null,
        date_end: obj.date_end ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.date_end, joinDate: "-", joinTime: ":"
            })) : null,
    });
    CourseAttendRateDashboard = async (obj: CourseRateReportParams): Promise<ResultObj<CourseAttendRateDashboardRes[]>> => await allFetch("Report/CourseAttendRateDashboard", {
        ...obj,
        ds_ids: obj.ds_ids || undefined,
        course_date_start: obj.course_date_start ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_start, joinDate: "-", joinTime: ":"
            })) : null,
        course_date_end: obj.course_date_end ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_end, joinDate: "-", joinTime: ":"
            })) : null,
    });
    CourseAnswerRateDashboard = async (obj: CourseRateReportParams): Promise<ResultObj<CourseAnswerRateDashboard[]>> => await allFetch("Report/CourseAnswerRateDashboard", {
        ...obj,
        ds_ids: obj.ds_ids || undefined,
        course_date_start: obj.course_date_start ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_start, joinDate: "-", joinTime: ":"
            })) : null,
        course_date_end: obj.course_date_end ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_end, joinDate: "-", joinTime: ":"
            })) : null,
    });
    CoursePretestAnswerRateDashboard = async (obj: CourseRateReportParams): Promise<ResultObj<CourseAnswerRateDashboard[]>> => await allFetch("Report/CoursePretestAnswerRateDashboard", {
        ...obj,
        ds_ids: obj.ds_ids || undefined,
        course_date_start: obj.course_date_start ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_start, joinDate: "-", joinTime: ":"
            })) : null,
        course_date_end: obj.course_date_end ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_end, joinDate: "-", joinTime: ":"
            })) : null,
    });
    CourseCompleteRateDashboard = async (obj: CourseRateReportParams): Promise<ResultObj<CourseCompleteRateDashboard[]>> => await allFetch("Report/CourseCompleteRateDashboard", {
        ...obj,
        ds_ids: obj.ds_ids || undefined,
        course_date_start: obj.course_date_start ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_start, joinDate: "-", joinTime: ":"
            })) : null,
        course_date_end: obj.course_date_end ?
            new Date(DateTimeFormate.dateFormate({
                dateStr: obj.course_date_end, joinDate: "-", joinTime: ":"
            })) : null,
    });
    StockUsageRankingReport = async (obj: StockUsageRankingReportParams): Promise<ResultObj<StockUsageRankingReportResItem[]>> => await allFetch("Report/StockUsageRankingReport", obj);
    EC_OrderSaleSummary = async (obj: EC_OrderSaleSummaryParams): Promise<ResultObj<EC_OrderSaleSummaryRes>> => await allFetch("Report/EC_OrderSaleSummary", obj);
    EC_OrderPromotionSummary = async (obj: EC_OrderPromotionSummaryParams): Promise<ResultObj<EC_OrderPromotionSummaryRes>> => await allFetch("Report/EC_OrderPromotionSummary", obj);
    EC_CustomerSummary = async (obj: EC_CustomerSummaryParams): Promise<ResultObj<EC_CustomerSummaryRes>> => await allFetch("Report/EC_CustomerSummary", obj);
    SafeStorageAlert = async (obj: SafeStorageAlertParams): Promise<ResultObj<SafeStorageAlertRes>> => await allFetch("Report/SafeStorageAlert", obj);
    EC_StockSluggishSale = async (obj: EC_StockSluggishSaleParams): Promise<ResultObj<EC_StockSluggishSaleRes>> => await allFetch("Report/EC_StockSluggishSale", obj);
    EC_OrderRevenueDetail = async (obj: EC_OrderRevenueDetailParams): Promise<ResultObj<EC_OrderRevenueDetailResItem[]>> => await allFetch("Report/EC_OrderRevenueDetail", obj);
    EC_OrderInvoiceDetail = async (obj: EC_OrderInvoiceDetailParams): Promise<ResultObj<EC_OrderInvoiceDetailResItem[]>> => await allFetch("Report/EC_OrderInvoiceDetail", obj);
    EC_OrderSaleDetail = async (obj: EC_OrderSaleDetailParams): Promise<ResultObj<EC_OrderSaleDetailResItem[]>> => await allFetch("Report/EC_OrderSaleDetail", obj);
    StockTransferDetailReport = async (obj: StockTransferDetailReportParams): Promise<ResultObj<StockTransferDetailReportResItem[]>> => await allFetch("Report/StockTransferDetailReport", obj);
    RollCallStatistic = async (obj: RollCallStatisticParams): Promise<ResultObj<RollCallStatisticResItem[]>> => await allFetch("Report/RollCallStatistic", {
        ...obj,
        ds_id: normalizeDsId(obj.ds_id)
    });
    UserRollCallStatistic = async (obj: UserRollCallStatisticParams): Promise<ResultObj<UserRollCallStatisticResItem[]>> => await allFetch("Report/UserRollCallStatistic", {
        ...obj,
        ds_id: normalizeDsId(obj.ds_id)
    });
}

class ReportDotNet6Adapter extends ReportDefaultAdapter {
    AttendanceStatistic = async (obj: AttendanceStatisticParams): Promise<ResultObj<AttendanceStatisticResItem[]>> => await allFetch("Report/AttendanceStatistic", {
        ...obj,
        date_start: defaultDateToDotNet6Format(obj.date_start),
        date_end: defaultDateToDotNet6Format(obj.date_end),
    });
    ScheduleXEmployeeReport = async (obj: ScheduleXEmployeeReportParams): Promise<ResultObj<ScheduleXEmployeeReportResItem[]>> => await allFetch("Report/ScheduleXEmployeeReport", {
        ...obj,
        date_start: defaultDateToDotNet6Format(obj.date_start),
        date_end: defaultDateToDotNet6Format(obj.date_end),
    });
    LeaveRecordReport = async (obj: LeaveRecordReportParams): Promise<ResultObj<LeaveRecordReportResItem[]>> => await allFetch("Report/LeaveRecordReport", {
        ...obj,
        date_start: defaultDateToDotNet6Format(obj.date_start),
        date_end: defaultDateToDotNet6Format(obj.date_end),
    });
    RollCallStatistic = async (obj: RollCallStatisticParams): Promise<ResultObj<RollCallStatisticResItem[]>> => await allFetch("Report/RollCallStatistic", {
        ...obj,
        ds_id: normalizeDsId(obj.ds_id),
        rollcall_date_start: defaultDateTimeToDotNet6Format(obj.rollcall_date_start),
        rollcall_date_end: defaultDateTimeToDotNet6Format(obj.rollcall_date_end),
    });
    UserRollCallStatistic = async (obj: UserRollCallStatisticParams): Promise<ResultObj<UserRollCallStatisticResItem[]>> => await allFetch("Report/UserRollCallStatistic", {
        ...obj,
        ds_id: normalizeDsId(obj.ds_id),
        rollcall_date_start: defaultDateTimeToDotNet6Format(obj.rollcall_date_start),
        rollcall_date_end: defaultDateTimeToDotNet6Format(obj.rollcall_date_end),
    });
    WIPIncomeClassificationReport = async (obj: WIPIncomeClassificationReportParams): Promise<ResultObj<WIPIncomeClassificationReportResItem[]>> => await allFetch("Report/WIPIncomeClassificationReport", {
        ...obj,
        start_date: defaultDateTimeToDotNet6Format(obj.start_date),
        end_date: defaultDateTimeToDotNet6Format(obj.end_date),
    });
    StockUsageRankingReport = async (obj: StockUsageRankingReportParams): Promise<ResultObj<StockUsageRankingReportResItem[]>> => await allFetch("Report/StockUsageRankingReport", {
        ...obj,
        start_date: defaultDateTimeToDotNet6Format(obj.start_date),
        end_date: defaultDateTimeToDotNet6Format(obj.end_date),
    });
    EC_OrderSaleSummary = async (obj: EC_OrderSaleSummaryParams): Promise<ResultObj<EC_OrderSaleSummaryRes>> => await allFetch("Report/EC_OrderSaleSummary", {
        ...obj,
        cre_date_start: defaultDateTimeToDotNet6Format(obj.cre_date_start),
        cre_date_end: defaultDateTimeToDotNet6Format(obj.cre_date_end),
    });
    EC_OrderPromotionSummary = async (obj: EC_OrderPromotionSummaryParams): Promise<ResultObj<EC_OrderPromotionSummaryRes>> => await allFetch("Report/EC_OrderPromotionSummary", {
        ...obj,
        cre_date_start: defaultDateTimeToDotNet6Format(obj.cre_date_start),
        cre_date_end: defaultDateTimeToDotNet6Format(obj.cre_date_end),
    });
    EC_CustomerSummary = async (obj: EC_CustomerSummaryParams): Promise<ResultObj<EC_CustomerSummaryRes>> => await allFetch("Report/EC_CustomerSummary", {
        ...obj,
        cre_date_start: defaultDateTimeToDotNet6Format(obj.cre_date_start),
        cre_date_end: defaultDateTimeToDotNet6Format(obj.cre_date_end),
    });
    EC_OrderRevenueDetail = async (obj: EC_OrderRevenueDetailParams): Promise<ResultObj<EC_OrderRevenueDetailResItem[]>> => await allFetch("Report/EC_OrderRevenueDetail", {
        ...obj,
        cre_date_start: defaultDateTimeToDotNet6Format(obj.cre_date_start),
        cre_date_end: defaultDateTimeToDotNet6Format(obj.cre_date_end),
    });
    EC_OrderInvoiceDetail = async (obj: EC_OrderInvoiceDetailParams): Promise<ResultObj<EC_OrderInvoiceDetailResItem[]>> => await allFetch("Report/EC_OrderInvoiceDetail", {
        ...obj,
        cre_date_start: defaultDateTimeToDotNet6Format(obj.cre_date_start),
        cre_date_end: defaultDateTimeToDotNet6Format(obj.cre_date_end),
        invoiceDate_start: defaultDateTimeToDotNet6Format(obj.invoiceDate_start),
        invoiceDate_end: defaultDateTimeToDotNet6Format(obj.invoiceDate_end),
    });
    EC_OrderSaleDetail = async (obj: EC_OrderSaleDetailParams): Promise<ResultObj<EC_OrderSaleDetailResItem[]>> => await allFetch("Report/EC_OrderSaleDetail", {
        ...obj,
        cre_date_start: defaultDateTimeToDotNet6Format(obj.cre_date_start),
        cre_date_end: defaultDateTimeToDotNet6Format(obj.cre_date_end),
        invoiceDate_start: defaultDateTimeToDotNet6Format(obj.invoiceDate_start),
        invoiceDate_end: defaultDateTimeToDotNet6Format(obj.invoiceDate_end),
    });
    StockTransferDetailReport = async (obj: StockTransferDetailReportParams): Promise<ResultObj<StockTransferDetailReportResItem[]>> => await allFetch("Report/StockTransferDetailReport", {
        ...obj,
        cre_date_start: defaultDateTimeToDotNet6Format(obj.cre_date_start),
        cre_date_end: defaultDateTimeToDotNet6Format(obj.cre_date_end),
        date_start: defaultDateTimeToDotNet6Format(obj.date_start),
        date_end: defaultDateTimeToDotNet6Format(obj.date_end),
    });
}

class ReportFrameworkAdapter extends ReportDefaultAdapter {
    // 即時庫存報表：Framework API 使用舊版參數格式 (date, product_id)，需從 .NET 6 格式轉換
    InTimeInventoryReport = async (obj: InTimeInventoryReportParams): Promise<ResultObj> => await allFetch("Report/InTimeInventoryReport", {
        date: obj.start_date,
        product_id: obj.commodity_id,
        excel: obj.excel,
    });
    emptyAdapter = async (): Promise<ResultObj> => ({ success: false, message: 'API未實現', data: null });
    RollCallStatistic = async (obj: RollCallStatisticParams): Promise<ResultObj<RollCallStatisticResItem[]>> => this.emptyAdapter();
    UserRollCallStatistic = async (obj: UserRollCallStatisticParams): Promise<ResultObj<UserRollCallStatisticResItem[]>> => this.emptyAdapter();
    WorkClockRecordStatistic = async (obj: WorkClockRecordStatisticParams): Promise<ResultObj<WorkClockRecordStatisticResItem[]>> => {
        const result: ResultObj<FrameworkWorkClockRecordStatisticResItem[]> = await allFetch("Report/WorkClockRecordStatistic", {
            ...obj,
            page: obj.page !== undefined ? String(obj.page) : undefined,
            num_per_page: obj.num_per_page !== undefined ? String(obj.num_per_page) : undefined,
        });
        if (!result.success || !result.data) return result as unknown as ResultObj<WorkClockRecordStatisticResItem[]>;
        return {
            ...result,
            data: result.data.map(({ DATE, ...rest }) => ({ ...rest, date: DATE })),
        } as unknown as ResultObj<WorkClockRecordStatisticResItem[]>;
    };
}

// #region EC_OrderSaleSummary

// /** EC訂單銷售總表 (查詢參數)
//  * @param cre_date_start 建立日期起
//  * @param cre_date_end 建立日期迄
//  * @param logistics_id 物流流水號
//  * @param position_id 據點流水號
//  * @param officesite_pay_statuses 官網付款狀態們
//  * @param officesite_order_statuses 官網訂單狀態們
//  * @param pay_statuses 付款狀態們
//  * @param pick_statuses 揀貨狀態們
//  * @param ship_statuses 出貨狀態們
//  * @param order_statuses 訂單狀態們
//  */
// export type EC_OrderSaleSummaryParams = {
//     cre_date_start?: string;
//     cre_date_end?: string;
//     logistics_id?: number;
//     position_id?: string;
//     officesite_pay_statuses?: string[];
//     officesite_order_statuses?: string[];
//     pay_statuses?: string[];
//     pick_statuses?: string[];
//     ship_statuses?: string[];
//     order_statuses?: string[];
// }

/** EC訂單銷售總表 月份統計項目
 * @param month 日期
 * @param deal_price 成交額
 * @param turnover_price 營業額
 * @param customer_count 顧客數
 * @param deal_count 成交訂單數
 * @param order_count 總訂單數
 * @param average_order_price 平均訂單金額
 */
export type EC_OrderSaleSummaryListItem = {
    month?: string;
    deal_price?: number;
    turnover_price?: number;
    customer_count?: number;
    deal_count?: number;
    order_count?: number;
    average_order_price?: number;
}

// /** EC訂單銷售總表 (回傳)
//  * @param order_create_today 本日新增訂單
//  * @param order_create_growth_rate 訂單成長率
//  * @param order_cancel_count 本日取消的訂單數
//  * @param order_return_count 本日退貨的訂單數
//  * @param order_wait_to_ship_count 訂單待出貨數
//  * @param order_sale_summary_list 銷售統計物件
//  */
// export type EC_OrderSaleSummaryRes = {
//     order_create_today?: number;
//     order_create_growth_rate?: number;
//     order_cancel_count?: number;
//     order_return_count?: number;
//     order_wait_to_ship_count?: number;
//     order_sale_summary_list?: EC_OrderSaleSummaryListItem[];
// }

// #endregion
// #region EC_OrderPromotionSummary

// /** EC訂單促銷統計 (查詢參數)
//  * @param cre_date_start 建立日期起
//  * @param cre_date_end 建立日期迄
//  * @param promotion_ids 促銷流水號們（必填）
//  * @param position_id 據點流水號
//  */
// export type EC_OrderPromotionSummaryParams = {
//     cre_date_start?: string;
//     cre_date_end?: string;
//     promotion_ids: string[];
//     position_id?: string;
// }

/** EC訂單促銷統計 月份促銷項目
 * @param month 日期
 * @param promotion_id 促銷流水號
 * @param promotion_name 促銷名稱
 * @param deal_price 成交額
 * @param deal_count 成交訂單數
 */
export type EC_OrderPromotionSummaryListItem = {
    month?: string;
    promotion_id?: string;
    promotion_name?: string;
    deal_price?: number;
    deal_count?: number;
}

/** 會員統計 月份項目
 * @param month 日期
 * @param create_count 新增數量
 */
export type EC_CustomerSummaryListItem = {
    month?: string;
    create_count?: number;
}

// /** EC訂單促銷統計 (回傳)
//  * @param order_create_today 本日新增訂單
//  * @param order_create_growth_rate 訂單成長率
//  * @param order_cancel_count 本日取消的訂單數
//  * @param order_return_count 本日退貨的訂單數
//  * @param order_wait_to_ship_count 訂單待出貨數
//  * @param order_promotion_summary_list 促銷統計物件
//  * @param customer_summary_list 會員統計物件
//  */
// export type EC_OrderPromotionSummaryRes = {
//     order_create_today?: number;
//     order_create_growth_rate?: number;
//     order_cancel_count?: number;
//     order_return_count?: number;
//     order_wait_to_ship_count?: number;
//     order_promotion_summary_list?: EC_OrderPromotionSummaryListItem[];
//     customer_summary_list?: EC_CustomerSummaryListItem[];
// }

// #endregion
// #region EC_CustomerSummary

// /** EC會員統計 (查詢參數)
//  * @param cre_date_start 建立日期起
//  * @param cre_date_end 建立日期迄
//  * @param position_id 據點流水號
//  */
// export type EC_CustomerSummaryParams = {
//     cre_date_start?: string;
//     cre_date_end?: string;
//     position_id?: string;
// }

// /** EC會員統計 (回傳)
//  * @param order_create_today 本日新增訂單
//  * @param order_create_growth_rate 訂單成長率
//  * @param order_cancel_count 本日取消的訂單數
//  * @param order_return_count 本日退貨的訂單數
//  * @param order_wait_to_ship_count 訂單待出貨數
//  * @param customer_summary_list 會員統計物件
//  */
// export type EC_CustomerSummaryRes = {
//     order_create_today?: number;
//     order_create_growth_rate?: number;
//     order_cancel_count?: number;
//     order_return_count?: number;
//     order_wait_to_ship_count?: number;
//     customer_summary_list?: EC_CustomerSummaryListItem[];
// }

// #endregion
// #region SafeStorageAlert

// /** 安全庫存查詢 (查詢參數)
//  * @param position_id 據點流水號
//  * @param page 頁碼
//  * @param num_per_page 一頁幾筆
//  */
// export type SafeStorageAlertParams = {
//     position_id?: string;
//     page?: number;
//     num_per_page?: number;
// }

/** 安全庫存查詢 商品項目
 * @param commodity_id 商品流水號
 * @param commodity_code 商品編號
 * @param commodity_name 商品名稱
 * @param convert_unit_name 換算單位名稱
 * @param convert_count 換算數量
 * @param basic_unit_name 基本單位名稱
 * @param basic_count 基本數量
 * @param uc_id 單位換算流水號
 * @param ccam_id_1 自訂屬性主項流水號1
 * @param ccam_name_1 自訂屬性主項名稱1
 * @param ccam_id_2 自訂屬性主項流水號2
 * @param ccam_name_2 自訂屬性主項名稱2
 * @param ccad_id_1 自訂屬性細項流水號1
 * @param ccad_name_1 自訂屬性細項名稱1
 * @param ccad_id_2 自訂屬性細項流水號2
 * @param ccad_name_2 自訂屬性細項名稱2
 * @param position_id 據點流水號
 * @param position_name 據點名稱
 * @param count 數量
 * @param safe_storage 安全庫存
 * @param filepath_headshot 大頭照路徑
 */
export type SafeStorageAlertListItem = {
    commodity_id?: string;
    commodity_code?: string;
    commodity_name?: string;
    convert_unit_name?: string;
    convert_count?: number;
    basic_unit_name?: string;
    basic_count?: number;
    uc_id?: string;
    ccam_id_1?: string;
    ccam_name_1?: string;
    ccam_id_2?: string;
    ccam_name_2?: string;
    ccad_id_1?: string;
    ccad_name_1?: string;
    ccad_id_2?: string;
    ccad_name_2?: string;
    position_id?: string;
    position_name?: string;
    count?: number;
    safe_storage?: number;
    filepath_headshot?: string;
}

// /** 安全庫存查詢 (回傳)
//  * @param order_create_today 本日新增訂單
//  * @param order_create_growth_rate 訂單成長率
//  * @param order_cancel_count 本日取消的訂單數
//  * @param order_return_count 本日退貨的訂單數
//  * @param order_wait_to_ship_count 訂單待出貨數
//  * @param order_safe_storage_alert_list 安全庫存警告物件
//  */
// export type SafeStorageAlertRes = {
//     order_create_today?: number;
//     order_create_growth_rate?: number;
//     order_cancel_count?: number;
//     order_return_count?: number;
//     order_wait_to_ship_count?: number;
//     order_safe_storage_alert_list?: SafeStorageAlertListItem[];
// }

// #endregion
// #region EC_StockSluggishSale

// /** 庫存滯銷查詢 (查詢參數)
//  * @param sluggish_day 滯銷天數
//  * @param position_id 據點流水號
//  * @param page 頁碼
//  * @param num_per_page 一頁幾筆
//  */
// export type EC_StockSluggishSaleParams = {
//     sluggish_day: number;
//     position_id?: string;
//     page?: number;
//     num_per_page?: number;
// }

/** 庫存滯銷查詢 商品項目
 * @param commodity_id 商品流水號
 * @param commodity_code 商品編號
 * @param commodity_name 商品名稱
 * @param convert_unit_name 換算單位名稱
 * @param convert_count 換算數量
 * @param basic_unit_name 基本單位名稱
 * @param basic_count 基本數量
 * @param uc_id 單位換算流水號
 * @param ccam_id_1 自訂屬性主項流水號1
 * @param ccam_name_1 自訂屬性主項名稱1
 * @param ccam_id_2 自訂屬性主項流水號2
 * @param ccam_name_2 自訂屬性主項名稱2
 * @param ccad_id_1 自訂屬性細項流水號1
 * @param ccad_name_1 自訂屬性細項名稱1
 * @param ccad_id_2 自訂屬性細項流水號2
 * @param ccad_name_2 自訂屬性細項名稱2
 * @param position_id 據點流水號
 * @param position_name 據點名稱
 * @param warehouse_name 倉庫名稱
 * @param warehouse_storage_space 倉庫儲位
 * @param sluggish_day 滯銷天數
 * @param filepath_headshot 大頭照路徑
 */
export type EC_StockSluggishSaleListItem = {
    commodity_id?: string;
    commodity_code?: string;
    commodity_name?: string;
    convert_unit_name?: string;
    convert_count?: number;
    basic_unit_name?: string;
    basic_count?: number;
    uc_id?: string;
    ccam_id_1?: string;
    ccam_name_1?: string;
    ccam_id_2?: string;
    ccam_name_2?: string;
    ccad_id_1?: string;
    ccad_name_1?: string;
    ccad_id_2?: string;
    ccad_name_2?: string;
    position_id?: string;
    position_name?: string;
    warehouse_name?: string;
    warehouse_storage_space?: string;
    sluggish_day?: number;
    filepath_headshot?: string;
}

// /** 庫存滯銷查詢 (回傳)
//  * @param order_create_today 本日新增訂單
//  * @param order_create_growth_rate 訂單成長率
//  * @param order_cancel_count 本日取消的訂單數
//  * @param order_return_count 本日退貨的訂單數
//  * @param order_wait_to_ship_count 訂單待出貨數
//  * @param order_stock_sluggish_sale_list 滯銷警告物件
//  */
// export type EC_StockSluggishSaleRes = {
//     order_create_today?: number;
//     order_create_growth_rate?: number;
//     order_cancel_count?: number;
//     order_return_count?: number;
//     order_wait_to_ship_count?: number;
//     order_stock_sluggish_sale_list?: EC_StockSluggishSaleListItem[];
// }

// #endregion
// #region WorkClockRecordStatistic
export interface WorkClockRecordStatisticParams {
    date_start: string;
    date_end: string;
    status?: string; // 打卡狀態
    user_ids?: string[];
    company_id?: string;
    general_manager_id?: string;
    department_id?: string;
    position_id?: string;
    class_id?: string;
    group_id?: string;
    office_id?: string;
    excel?: string;
    page?: number;
    num_per_page?: number;
}

/** Framework版本的 WorkClockRecordStatistic 回傳結構 (僅用於framework adapter 內部轉換) */
interface FrameworkWorkClockRecordStatisticResItem {
    DATE: string;
    user_id: string;
    name: string;
    username: string;
    sc_name: string;
    time_to: string;
    place_to: string;
    status_to: string;
    time_off: string;
    place_off: string;
    status_off: string;
    note: string;
}

/**
 * @description 打卡記錄統計報表 (回傳)
 * @param {string} date 日期
 * @param {string} user_id 使用者流水號
 * @param {string} name 姓名
 * @param {string} username 帳號
 * @param {string} sc_name 班別名稱
 * @param {string} time_to 上班打卡時間
 * @param {string} place_to 上班打卡地點
 * @param {string} status_to 上班打卡狀態
 * @param {string} time_off 下班打卡時間
 * @param {string} place_off 下班打卡地點
 * @param {string} status_off 下班打卡狀態
 * @param {string} note 備注
 */
export interface WorkClockRecordStatisticResItem {
    date: string;
    user_id: string;
    name: string;
    username: string;
    sc_name: string;
    time_to: string;
    place_to: string;
    status_to: string;
    time_off: string;
    place_off: string;
    status_off: string;
    note: string;
}
// #endregion
// #region AttendanceStatistic
/**
 * @description 考勤表 (參數)
 * @param date_start 日期起
 * @param date_end 日期迄
 * @param user_ids 帳號流水號們
 * @param company_id 公司流水號
 * @param general_manager_id 高階管理階層流水號
 * @param department_id 部門流水號
 * @param position_id 廠別流水號
 * @param class_id 課級流水號
 * @param group_id 組級流水號
 * @param office_id 辦公室流水號
 * @param check_auditing 檢查審核中申請單
 * @param excel 是否匯出
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 */
export interface AttendanceStatisticParams {
    date_start: string;
    date_end: string;
    user_ids?: string[];
    company_id?: string;
    general_manager_id?: string;
    department_id?: string;
    position_id?: string;
    class_id?: string;
    group_id?: string;
    office_id?: string;
    check_auditing?: string;
    excel?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 考勤表 (回傳) — 動態欄位，key 為欄位名稱
 */
export type AttendanceStatisticResItem = {
    [key: string]: number;
}
// #endregion
// #region ScheduleXEmployeeReport
/**
 * @description 排班結果報表 (參數)
 * @param date_start 日期起
 * @param date_end 日期迄
 * @param user_ids 帳號流水號們
 * @param company_id 公司流水號
 * @param general_manager_id 高階管理階層流水號
 * @param department_id 部門流水號
 * @param position_id 廠別流水號
 * @param class_id 課級流水號
 * @param group_id 組級流水號
 * @param office_id 辦公室流水號
 * @param excel 是否匯出
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 */
export interface ScheduleXEmployeeReportParams {
    date_start: string;
    date_end: string;
    user_ids?: string[];
    company_id?: string;
    general_manager_id?: string;
    department_id?: string;
    position_id?: string;
    class_id?: string;
    group_id?: string;
    office_id?: string;
    excel?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 排班結果報表 (回傳) — 動態欄位，key 為欄位名稱
 */
export interface ScheduleXEmployeeReportResItem {
    year: number;
    month: number;
    day: number;
    weekday: string;
    user_id: string;
    name: string;
    place_name: string;
    sc_code: string;
    [key: string]: any;
}
// #endregion
// #region LeaveRecordReport
/**
 * @description 請假紀錄報表 (參數)
 * @param date_start 日期起
 * @param date_end 日期迄
 * @param user_ids 帳號流水號們
 * @param company_id 公司流水號
 * @param general_manager_id 高階管理階層流水號
 * @param department_id 部門流水號
 * @param position_id 廠別流水號
 * @param class_id 課級流水號
 * @param group_id 組級流水號
 * @param office_id 辦公室流水號
 * @param excel 是否匯出
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 */

export interface LeaveRecordReportParams {
    date_start: string;
    date_end: string;
    user_ids?: string[];
    company_id?: string;
    general_manager_id?: string;
    department_id?: string;
    position_id?: string;
    class_id?: string;
    group_id?: string;
    office_id?: string;
    excel?: string;
    page?: number;
    num_per_page?: number;
}

// #endregion
// #region LeaveRemainAnnualReport
/**
 * @description 請假紀錄報表 (回傳) — 動態欄位，key 為欄位名稱
 */
export type LeaveRecordReportResItem = {
    [key: string]: number;
}

/**
 * @description 剩餘特休報表 (參數)
 * @param isresign 是否離職
 * @param user_ids 帳號流水號們
 * @param company_id 公司流水號
 * @param general_manager_id 高階管理階層流水號
 * @param department_id 部門流水號
 * @param position_id 廠別流水號
 * @param class_id 課級流水號
 * @param group_id 組級流水號
 * @param office_id 辦公室流水號
 * @param excel 是否匯出
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 */

export interface LeaveRemainAnnualReportParams {
    user_ids?: string[];
    isresign?: string;
    company_id?: string;
    general_manager_id?: string;
    department_id?: string;
    position_id?: string;
    class_id?: string;
    group_id?: string;
    office_id?: string;
    excel?: string;
    page?: number;
    num_per_page?: number;
}

/** Framework ScheduleXEmployeeReport 原始回應欄位（中文 key，供 adapter 內部轉換使用） */
interface ScheduleXEmployeeReportFrameworkRawItem {
    年: number;
    月: number;
    日: number;
    星期: string;
    帳號: string;
    同仁姓名: string;
    值班地點: string;
    業務班別: string;
}

/** Framework LeaveRemainAnnualReport 原始回應欄位（中文 key，供 adapter 內部轉換使用） */
interface LeaveRemainAnnualReportFrameworkRawItem {
    姓名: string;
    帳號: string;
    身分證: string;
    到職日: string;
    年資: string;
    特休天數: number;
    取得日期: string;
    已使用天數: number;
    剩餘天數: number;
}

/**
 * @description 剩餘特休報表 (回傳)
 * @param Name 姓名
 * @param Username 帳號
 * @param IdentityCard 身分證
 * @param OnBoardDate 到職日
 * @param Year 年資
 * @param DayGet 特休天數
 * @param GetDate 取得日期
 * @param DayUsed 已使用天數
 * @param DayRemain 剩餘天數
 */
export interface LeaveRemainAnnualReportResItem {
    Name: string;
    Username: string;
    IdentityCard: string;
    OnBoardDate: string;
    Year: string;
    DayGet: number;
    GetDate: string;
    DayUsed: number;
    DayRemain: number;
    [key: string]: string | number;
}
// #endregion
// #region DispatchCompanyReport
export interface DispatchCompanyReportParams {
    position_id?: string;
    dispatch_company_id?: string;
    dispatch_company_code?: string;
    dispatch_company_name?: string;
    enter_date_start?: string;
    enter_date_end?: string;
    caseclose_date_start?: string;
    caseclose_date_end?: string;
    rld_category?: string; // 僅用於framework
    is_caseclose?: string; // 僅用於framework
    excel?: string;
    page?: number;
    num_per_page?: number;
}
export interface DispatchCompanyReportResItem {
    caseclose_time: string;
    dispatch_company_code: string;
    dispatch_company_cost: number;
    dispatch_company_name: string;
    license_plate: string;
    position_name: string;
    price: number;
    rld_category: string;
    rld_name: string;
    time_enter: string;
    wom_code: string;
}

// #endregion
// #region CountTestDriveByWeek
export interface CountTestDriveByWeekParams {
    start_date: string;
    end_date: string;
    excel: string;
}
// #endregion
// #region TechnicianPerformanceReport
export interface TechnicianPerformanceReportParams {
    user_id?: string,
    wom_code?: string,
    username?: string,
    start_date?: string,
    end_date?: string,
    caseclose_date_start?: string,
    caseclose_date_end?: string,
    rld_category?: string,
    excel?: string,
    page?: number,
    num_per_page?: number
}
export type TechnicianPerformanceReportResItem = {
    username: string
    dispatch_name: string
    total_work_hours: number
    total_price: number
}
// #endregion
// #region TechnicianPerformanceDetailReport
export interface TechnicianPerformanceDetailReportParams {
    user_id?: string;
    wom_code?: string;
    username?: string;
    start_date?: string;
    end_date?: string;
    caseclose_date_start?: string;
    caseclose_date_end?: string;
    excel?: string;
    page?: number;
    num_per_page?: number;
}
export type TechnicianPerformanceDetailReportResItem = {
    username: string
    dispatch_name: string
    wom_code: string
    rld_type: string
    rld_name: string
    work_hours_setting: number
    total_price: number
    pay_method: string
    complete_time: string
    caseclose_time: string
}
// #endregion
// #region IncomeClassificationReport
export interface IncomeClassificationReportResItem {
    DEP_name: string | null;
    caseclose_time: string;
    component: number,
    cost: number;
    deductible_price: number;
    outsource: number;
    paint: number;
    reception_cre_time: string;
    reception_name: string;
    sheet_metal: number;
    tax: number;
    total_income: number;
    user_id: string;
    wom_code: string;
    wom_type: string | null;
    wom_type_name: string | null;
}
export interface IncomeClassificationReportParams {
    wom_code?: string;
    username?: string;
    user_id?: string;
    start_date?: string;
    end_date?: string;
    rld_category?: string; // 僅用於framework
    wom_category?: string; // 僅用於framework
    wom_type_name?: string; // 僅用於framework
    DEP_name?: string; // 僅用於framework
    excel?: TypeYN;
    page?: number;
    num_per_page?: number;
}

/**
 * @description WIP收入分類明細表 (參數)
 * @param {string} wom_id 工單流水號
 * @param {string} wom_code 工單號碼
 * @param {string} start_date 日期起
 * @param {string} end_date 日期訖
 * @param {string[]} statuses 工單狀態
 * @param {string} excel 是否匯出
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface WIPIncomeClassificationReportParams {
    wom_id?: string;
    wom_code?: string;
    start_date?: string;
    end_date?: string;
    statuses?: string[];
    excel?: string;
    page?: number;
    num_per_page?: number;
}
/**
 * @description WIP收入分類明細表 (回傳)
 * @param {string} wom_id 工單流水號
 * @param {string} wom_code 工單號碼
 * @param {string} status 狀態
 * @param {string} license_plate 車牌號碼
 * @param {string} car_type 車型
 * @param {string} caseclose_time 結案時間
 * @param {number} cost 零件成本
 * @param {number} component 零件收入
 * @param {number} sheet_metal 鈑金收入
 * @param {number} paint 烤漆收入
 * @param {number} outsource 外包收入
 * @param {number} labor_income 工資收入
 * @param {number} income_total 收入合計
 * @param {number} tax 稅額
 * @param {string} time_enter 進廠時間
 */
export interface WIPIncomeClassificationReportResItem {
    wom_id: string;
    wom_code: string;
    status: string;
    license_plate: string;
    car_type: string;
    caseclose_time: string;
    cost: number;
    component: number;
    sheet_metal: number;
    paint: number;
    outsource: number;
    labor_income: number;
    income_total: number;
    tax: number;
    time_enter: string;
}

/**
 * @description WIP收入分類明細零件細項 (參數)
 * @param {string} wom_id 工單流水號
 * @param {string} page 頁碼
 * @param {string} num_per_page 一頁幾筆
 */
export interface WIPIncomeClassificationDetailParams {
    wom_id: string;
    page?: string;
    num_per_page?: string;
}

/**
 * @description WIP收入分類明細零件細項 (回傳)
 * @param {string} wom_id 工單流水號
 * @param {string} rld_code 零件代碼
 * @param {string} rld_name 零件名稱
 * @param {number} count 數量
 * @param {number} unit_cost 單一零件成本
 * @param {number} cost 單一零件成本總計
 * @param {number} sale_price 單一零件售價
 * @param {number} price 單一零件售價總計
 */
export interface WIPIncomeClassificationDetailResItem {
    wom_id: string;
    rld_code: string;
    rld_name: string;
    count: number;
    unit_cost: number;
    cost: number;
    sale_price: number;
    price: number;
}
// #endregion
// #region InTimeInventoryReport
/**
 * @description 即時庫存報表 (參數) — 以 .NET 6 為主；frameworkAdapter 會將新格式轉換為舊格式
 * @param {string} commodity_id 商品流水號 (舊版 product_id)
 * @param {string} commodity_name 商品名稱
 * @param {string} start_date 日期起 (舊版 date)
 * @param {string} end_date 日期訖
 * @param {string} excel 是否匯出
 * @param {string} page 頁碼
 * @param {string} num_per_page 一頁幾筆
 */
export interface InTimeInventoryReportParams {
    commodity_id?: string;
    commodity_name?: string;
    start_date?: string;
    end_date?: string;
    excel?: string;
    page?: string;
    num_per_page?: string;
}

// #endregion
// #region RepairListDetailReport
export interface RepairListDetailReportParams {
    date_start?: string; // 僅用於framework
    date_end?: string; // 僅用於framework
    technician_id?: string; // 僅用於framework
    excel?: string; // 僅用於framework
}
// #endregion
// #region UserReport
export interface UserReportParams {
    username?: string;
    name?: string;
    company_id?: string;
    general_manager_id?: string;
    department_id?: string;
    position_id?: string;
    class_id?: string;
    group_id?: string;
    office_id?: string;
    disable?: string;
    isresign?: string;
    ul_id?: string;
    ul_name?: string;
}
/**
 * @description 使用者資訊報表 (回傳)
 */
export interface UserReportResItem { }

/** 工單付款記錄報表 (參數)
 * @param wom_code 工單號碼
 * @param wom_category 工單內外帳類別
 * @param status 狀態們
 * @param user_id 使用者流水號
 * @param position_id 據點流水號
 * @param customer_id 車主流水號
 * @param payment_DEP 請款部門
 * @param car_id 車流水號
 * @param license_plate 車牌號碼
 * @param time_enter_start 進廠時間起
 * @param time_enter_end 進廠時間迄
 * @param time_leave_start 離廠時間起
 * @param time_leave_end 離廠時間迄
 * @param insurance_company_id 保險公司流水號
 * @param is_complete 是否完工
 * @param complete_time_start 完工時間起
 * @param complete_time_end 完工時間迄
 * @param is_caseclose 是否結案
 * @param caseclose_time_start 結案時間起
 * @param caseclose_time_end 結案時間迄
 * @param is_invalid 是否作廢
 * @param invalid_time_start 作廢時間起
 * @param invalid_time_end 作廢時間迄
 * @param invoice_number 發票號碼
 * @param is_panelpaint 是否鈑烤
 * @param wom_type 工單請款類別
 * @param excel 是否匯出
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 */
// #endregion
// #region WorkOrderPayLogReport
export interface WorkOrderPayLogReportParams {
    wom_code?: string; // 僅用於framework
    wom_category?: string; // 僅用於framework
    status?: string[]; // 僅用於framework
    user_id?: string; // 僅用於framework
    position_id?: string; // 僅用於framework
    customer_id?: string; // 僅用於framework
    payment_DEP?: string; // 僅用於framework
    car_id?: string; // 僅用於framework
    license_plate?: string; // 僅用於framework
    time_enter_start?: string; // 僅用於framework
    time_enter_end?: string; // 僅用於framework
    time_leave_start?: string; // 僅用於framework
    time_leave_end?: string; // 僅用於framework
    insurance_company_id?: string; // 僅用於framework
    is_complete?: string; // 僅用於framework
    complete_time_start?: string; // 僅用於framework
    complete_time_end?: string; // 僅用於framework
    is_caseclose?: string; // 僅用於framework
    caseclose_time_start?: string; // 僅用於framework
    caseclose_time_end?: string; // 僅用於framework
    is_invalid?: string; // 僅用於framework
    invalid_time_start?: string; // 僅用於framework
    invalid_time_end?: string; // 僅用於framework
    invoice_number?: string; // 僅用於framework
    is_panelpaint?: string; // 僅用於framework
    wom_type?: string; // 僅用於framework
    excel?: string; // 僅用於framework
    page?: number; // 僅用於framework
    num_per_page?: number; // 僅用於framework
}
// #endregion
// #region UserGenderDashboard
/** 帳號性別統計參數
 * @param on_board_date_start 入職日期起
 * @param on_board_date_end 入職日期迄
 * @param ds_ids 經銷商id們
 * @param ul_ids 職務id們
 */
export interface UserGenderDashboardParams {
    on_board_date_start: string;
    on_board_date_end: string;
    ds_ids: number[];
    ul_ids: number[];
}

/** 帳號性別統計回傳
 * @param gender_name 性別
 * @param gender_count 數量
 * @param gender_rate 百分比
 */
export interface UserGenderDashboardRes {
    gender_name: string;
    gender_count: number;
    gender_rate: number;
}
// #endregion
// #region UserResignDashboard
/** 帳號離職統計參數
 * @param date_start 日期起
 * @param date_end 日期迄
 * @param ds_ids 經銷商id們
 * @param ul_ids 職務id們
 */
export interface UserResignDashboardParams {
    date_start: string;
    date_end: string;
    ds_ids: number[];
    ul_ids: number[];
}

/** 帳號離職統計回傳
 * @param ds_name 經銷商名稱
 * @param year 年
 * @param month 月
 * @param resign_rate 離職率
 */
export interface UserResignDashboardRes {
    ds_name: string,
    year: number,
    month: number,
    resign_rate: number
}
// #endregion
// #region CourseDashboard
/** 課程統計報表參數
 * @param ds_ids 經銷商id們
 * @param course_ids 課程id們
 * @param course_schedule_ids 課程id們
 * @param course_date_start 課程日期起
 * @param course_date_end 課程日期迄
 */
export interface CourseDashboardParams {
    ds_ids?: number[];
    course_type_id?: string;
    course_ids?: string[];
    course_schedule_ids?: string[];
    course_date_start?: string;
    course_date_end?: string;
}

/** 課程統計報表回傳
 * @param attend_rate_list 出席率
 * @param answer_rate_list 答題率
 * @param pretest_answer_rate_list 課前測驗答題率
 * @param complete_rate_list 完成率
 */
export interface CourseDashboardRes {
    attend_rate_list: CourseItem[] | null;
    answer_rate_list: CourseItem[] | null;
    pretest_answer_rate_list: CourseItem[] | null;
    complete_rate_list: CourseItem[] | null;
}

/** 課程統計報表 => 課程資訊
 * @param course_name 課程名稱
 * @param course_schedule_name 課程梯次
 * @param dealer_list 經銷商列表
 */
export interface CourseItem {
    course_id: string;
    course_name: string;
    course_type_name: string;
    course_schedule_name: string;
    dealer_list: StaticItem[];
}

/** 課程統計報表 => 統計數字
 * @param ds_name 經銷商名稱
 * @param static_rate 統計數字
 */
export interface StaticItem {
    ds_id: number;
    ds_name: string;
    static_rate: number;
}
// #endregion
// #region CourseAttendRateDashboard
/** 課程：出席率、作答率、完成率查詢參數
 * @param ds_ids 經銷商id們
 * @param course_type_id 課程類型id
 * @param course_ids 課程id們
 * @param course_schedule_ids 課程梯次id們
 * @param course_date_start 課程日期起
 * @param course_date_end 課程日期迄
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 */
export interface CourseRateReportParams {
    ds_ids?: number[],
    course_type_id?: string,
    course_ids?: string[],
    course_schedule_ids?: string[],
    course_date_start?: string,
    course_date_end?: string,
    page: number,
    num_per_page: number
}
/** 課程出席率報表結果
 * @param ds_id 經銷商id
 * @param ds_name 經銷商名稱
 * @param course_id 課程id
 * @param course_name 課程名稱
 * @param course_type_name 課程類型名稱
 * @param course_schedule_name 課程梯次名稱
 * @param course_date_start 課程日期
 * @param attendance_count 出席人數
 * @param expected_attendance 預計出席人數
 * @param attend_rate 出席率
 */
export interface CourseAttendRateDashboardRes {
    ds_id: number | null;
    ds_name: string | null;
    course_id: string | null;
    course_name: string | null;
    course_type_name: string | null;
    course_schedule_name: string | null;
    course_date_start: string | null;
    attendance_count: number;
    expected_attendance: number;
    attend_rate: number
}
// #endregion
// #region CourseAnswerRateDashboard
/** 課程作答率報表結果
 * @param ds_id 經銷商id
 * @param ds_name 經銷商名稱
 * @param course_id 課程id
 * @param course_name 課程名稱
 * @param course_type_name 課程類型名稱
 * @param course_schedule_id 課程梯次id
 * @param course_schedule_name 課程梯次名稱
 * @param course_date_start 課程日期
 * @param response_count 作答人數
 * @param attendance_count 出席人數
 * @param answer_rate 作答率
 */
export interface CourseAnswerRateDashboard {
    ds_id: number | null;
    ds_name: string | null;
    course_id: string | null;
    course_name: string | null;
    course_type_name: string | null;
    course_schedule_id: string | null;
    course_schedule_name: string | null;
    course_date_start: string | null;
    response_count: number;
    attendance_count: number;
    answer_rate: number;
}
// #endregion
// #region CoursePretestAnswerRateDashboard
/** 課程課前考試作答率統計表 (查詢參數)
 * @param ds_ids 經銷商流水號們
 * @param course_type_id 課程類型
 * @param course_ids 課程流水號們
 * @param course_schedule_ids 開課紀錄流水號們
 * @param course_date_start 課程日期起
 * @param course_date_end 課程日期迄
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 */
export type CoursePretestAnswerRateDashboardParams = {
    ds_ids?: number[];
    course_type_id?: string;
    course_ids?: string[];
    course_schedule_ids?: string[];
    course_date_start?: string;
    course_date_end?: string;
    page?: number;
    num_per_page?: number;
}
// #endregion
// #region CourseCompleteRateDashboard
/** 課程完成率報表結果
 * @param ds_id 經銷商id
 * @param ds_name 經銷商名稱
 * @param course_id 課程id
 * @param course_name 課程名稱
 * @param course_type_name 課程類型名稱
 * @param course_schedule_name 課程梯次名稱
 * @param course_date_start 課程日期
 * @param complete_count 完成人數
 * @param expected_attendance 預計出席人數
 * @param complete_rate 完成率
 */
export interface CourseCompleteRateDashboard {
    ds_id: number | null;
    ds_name: string | null;
    course_id: string | null;
    course_name: string | null;
    course_type_name: string | null;
    course_schedule_name: string | null;
    course_date_start: string | null;
    complete_count: number;
    expected_attendance: number;
    complete_rate: number;
}
// #endregion



// ==================== 以下為新增 .NET 6 Endpoint 型態定義 ====================

/**
 * @description 訂單銷售總表 (參數)
 * @param {string} cre_date_start 建立日期起
 * @param {string} cre_date_end 建立日期訖
 * @param {number} logistics_id 物流商流水號
 * @param {string} position_id 據點流水號
 * @param {string[]} officesite_pay_statuses 線下付款狀態們
 * @param {string[]} officesite_order_statuses 線下訂單狀態們
 * @param {string[]} pay_statuses 付款狀態們
 * @param {string[]} pick_statuses 取貨狀態們
 * @param {string[]} ship_statuses 出貨狀態們
 * @param {string[]} order_statuses 訂單狀態們
 */
export interface EC_OrderSaleSummaryParams {
    cre_date_start?: string;
    cre_date_end?: string;
    logistics_id?: number | null;
    position_id?: string;
    officesite_pay_statuses?: string[];
    officesite_order_statuses?: string[];
    pay_statuses?: string[];
    pick_statuses?: string[];
    ship_statuses?: string[];
    order_statuses?: string[];
}

/**
 * @description 訂單銷售總表列表項 (回傳子項)
 * @param {string} month 月份
 * @param {number} deal_price 成交金額
 * @param {number} turnover_price 營業額
 * @param {number} customer_count 客戶數
 * @param {number} deal_count 成交數
 * @param {number} order_count 訂單數
 * @param {number} average_order_price 平均客單價
 */
export interface EC_OrderSaleSummaryItem {
    month: string | null;
    deal_price: number | null;
    turnover_price: number | null;
    customer_count: number | null;
    deal_count: number | null;
    order_count: number | null;
    average_order_price: number | null;
}

/**
 * @description 訂單銷售總表 (回傳)
 * @param {number} order_create_today 今日建立訂單數
 * @param {number} order_create_growth_rate 訂單成長率
 * @param {number} order_cancel_count 取消訂單數
 * @param {number} order_return_count 退貨訂單數
 * @param {number} order_wait_to_ship_count 待出貨訂單數
 * @param {EC_OrderSaleSummaryItem[]} order_sale_summary_list 銷售統計列表
 */
export interface EC_OrderSaleSummaryRes {
    order_create_today: number | null;
    order_create_growth_rate: number | null;
    order_cancel_count: number | null;
    order_return_count: number | null;
    order_wait_to_ship_count: number | null;
    order_sale_summary_list: EC_OrderSaleSummaryItem[] | null;
}

/**
 * @description 訂單促銷統計 (參數)
 * @param {string} cre_date_start 建立日期起
 * @param {string} cre_date_end 建立日期訖
 * @param {string[]} promotion_ids 促銷活動流水號們 (必填)
 * @param {string} position_id 據點流水號
 */
export interface EC_OrderPromotionSummaryParams {
    cre_date_start?: string;
    cre_date_end?: string;
    promotion_ids: string[];
    position_id?: string;
}

/**
 * @description 訂單促銷統計列表項 (回傳子項)
 * @param {string} month 月份
 * @param {string} promotion_id 促銷活動流水號
 * @param {string} promotion_name 促銷活動名稱
 * @param {number} deal_price 成交金額
 * @param {number} deal_count 成交數
 */
export interface EC_OrderPromotionSummaryItem {
    month: string | null;
    promotion_id: string | null;
    promotion_name: string | null;
    deal_price: number | null;
    deal_count: number | null;
}

/**
 * @description 會員統計列表項 (回傳子項)
 * @param {string} month 月份
 * @param {number} create_count 新增會員數
 */
export interface EC_CustomerSummaryItem {
    month: string | null;
    create_count: number | null;
}

/**
 * @description 訂單促銷統計 (回傳)
 * @param {number} order_create_today 今日建立訂單數
 * @param {number} order_create_growth_rate 訂單成長率
 * @param {number} order_cancel_count 取消訂單數
 * @param {number} order_return_count 退貨訂單數
 * @param {number} order_wait_to_ship_count 待出貨訂單數
 * @param {EC_OrderPromotionSummaryItem[]} order_promotion_summary_list 促銷統計列表
 * @param {EC_CustomerSummaryItem[]} customer_summary_list 會員統計列表
 */
export interface EC_OrderPromotionSummaryRes {
    order_create_today: number | null;
    order_create_growth_rate: number | null;
    order_cancel_count: number | null;
    order_return_count: number | null;
    order_wait_to_ship_count: number | null;
    order_promotion_summary_list: EC_OrderPromotionSummaryItem[] | null;
    customer_summary_list: EC_CustomerSummaryItem[] | null;
}

/**
 * @description 會員統計 (參數)
 * @param {string} cre_date_start 建立日期起
 * @param {string} cre_date_end 建立日期訖
 * @param {string} position_id 據點流水號
 */
export interface EC_CustomerSummaryParams {
    cre_date_start?: string;
    cre_date_end?: string;
    position_id?: string;
}

/**
 * @description 會員統計 (回傳)
 * @param {number} order_create_today 今日建立訂單數
 * @param {number} order_create_growth_rate 訂單成長率
 * @param {number} order_cancel_count 取消訂單數
 * @param {number} order_return_count 退貨訂單數
 * @param {number} order_wait_to_ship_count 待出貨訂單數
 * @param {EC_CustomerSummaryItem[]} customer_summary_list 會員統計列表
 */
export interface EC_CustomerSummaryRes {
    order_create_today: number | null;
    order_create_growth_rate: number | null;
    order_cancel_count: number | null;
    order_return_count: number | null;
    order_wait_to_ship_count: number | null;
    customer_summary_list: EC_CustomerSummaryItem[] | null;
}

/**
 * @description 安全庫存查詢 (參數)
 * @param {string} position_id 據點流水號
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface SafeStorageAlertParams {
    position_id?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 點名統計 / 員工點名明細 共用查詢參數
 * @param ds_id 經銷商編號
 * @param ds_code 經銷商代碼
 * @param ds_name 經銷商名稱
 * @param rollcall_id 點名編號
 * @param rollcall_detail_id 點名細項編號
 * @param user_id 員工流水號
 * @param username 員工編號
 * @param name 員工名字
 * @param state 點名狀態
 * @param class_id 課級編號
 * @param class_name 課級名稱
 * @param sc_id 班別編號
 * @param rollcall_date_start 點名時間起
 * @param rollcall_date_end 點名時間迄
 * @param position_id 據點編號
 * @param sort_name 排序依據
 * @param sort_order 排序順序
 * @param excel 是否匯出
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 */
export interface RollCallStatisticParams {
    ds_id?: number;
    ds_code?: string;
    ds_name?: string;
    rollcall_id?: string;
    rollcall_detail_id?: number;
    user_id?: string;
    username?: string;
    name?: string;
    state?: string;
    class_id?: string;
    class_name?: string;
    sc_id?: string;
    rollcall_date_start?: string;
    rollcall_date_end?: string;
    position_id?: string;
    sort_name?: string;
    sort_order?: string;
    excel?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 安全庫存查詢列表項 (回傳子項)
 * @param {string} commodity_id 商品流水號
 * @param {string} commodity_code 商品代碼
 * @param {string} commodity_name 商品名稱
 * @param {string} convert_unit_name 換算單位名稱
 * @param {number} convert_count 換算單位數量
 * @param {string} basic_unit_name 基本單位名稱
 * @param {number} basic_count 基本單位數量
 * @param {string} uc_id 單位換算流水號
 * @param {string} ccam_id_1 商品分類主類別1流水號
 * @param {string} ccam_name_1 商品分類主類別1名稱
 * @param {string} ccam_id_2 商品分類主類別2流水號
 * @param {string} ccam_name_2 商品分類主類別2名稱
 * @param {string} ccad_id_1 商品分類細項1流水號
 * @param {string} ccad_name_1 商品分類細項1名稱
 * @param {string} ccad_id_2 商品分類細項2流水號
 * @param {string} ccad_name_2 商品分類細項2名稱
 * @param {string} position_id 據點流水號
 * @param {string} position_name 據點名稱
 * @param {number} count 庫存數量
 * @param {number} safe_storage 安全庫存量
 * @param {string} filepath_headshot 商品圖片路徑
 */
export interface SafeStorageAlertResItem {
    commodity_id: string | null;
    commodity_code: string | null;
    commodity_name: string | null;
    convert_unit_name: string | null;
    convert_count: number;
    basic_unit_name: string | null;
    basic_count: number;
    uc_id: string | null;
    ccam_id_1: string | null;
    ccam_name_1: string | null;
    ccam_id_2: string | null;
    ccam_name_2: string | null;
    ccad_id_1: string | null;
    ccad_name_1: string | null;
    ccad_id_2: string | null;
    ccad_name_2: string | null;
    position_id: string | null;
    position_name: string | null;
    count: number;
    safe_storage: number;
    filepath_headshot: string | null;
}

/**
 * @description 安全庫存查詢 (回傳)
 * @param {number} order_create_today 今日建立訂單數
 * @param {number} order_create_growth_rate 訂單成長率
 * @param {number} order_cancel_count 取消訂單數
 * @param {number} order_return_count 退貨訂單數
 * @param {number} order_wait_to_ship_count 待出貨訂單數
 * @param {SafeStorageAlertResItem[]} order_safe_storage_alert_list 安全庫存列表
 */
export interface SafeStorageAlertRes {
    order_create_today: number | null;
    order_create_growth_rate: number | null;
    order_cancel_count: number | null;
    order_return_count: number | null;
    order_wait_to_ship_count: number | null;
    order_safe_storage_alert_list: SafeStorageAlertResItem[] | null;
}

/**
 * @description 庫存滯銷查詢 (參數)
 * @param {number} sluggish_day 滯銷天數
 * @param {string} position_id 據點流水號
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface EC_StockSluggishSaleParams {
    sluggish_day: number;
    position_id?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 庫存滯銷查詢列表項 (回傳子項)
 * @param {string} commodity_id 商品流水號
 * @param {string} commodity_code 商品代碼
 * @param {string} commodity_name 商品名稱
 * @param {string} convert_unit_name 換算單位名稱
 * @param {number} convert_count 換算單位數量
 * @param {string} basic_unit_name 基本單位名稱
 * @param {number} basic_count 基本單位數量
 * @param {string} uc_id 單位換算流水號
 * @param {string} ccam_id_1 商品分類主類別1流水號
 * @param {string} ccam_name_1 商品分類主類別1名稱
 * @param {string} ccam_id_2 商品分類主類別2流水號
 * @param {string} ccam_name_2 商品分類主類別2名稱
 * @param {string} ccad_id_1 商品分類細項1流水號
 * @param {string} ccad_name_1 商品分類細項1名稱
 * @param {string} ccad_id_2 商品分類細項2流水號
 * @param {string} ccad_name_2 商品分類細項2名稱
 * @param {string} position_id 據點流水號
 * @param {string} position_name 據點名稱
 * @param {string} warehouse_name 倉庫名稱
 * @param {string} warehouse_storage_space 倉位
 * @param {number} sluggish_day 滯銷天數
 * @param {string} filepath_headshot 商品圖片路徑
 */
export interface EC_StockSluggishSaleResItem {
    commodity_id: string | null;
    commodity_code: string | null;
    commodity_name: string | null;
    convert_unit_name: string | null;
    convert_count: number;
    basic_unit_name: string | null;
    basic_count: number;
    uc_id: string | null;
    ccam_id_1: string | null;
    ccam_name_1: string | null;
    ccam_id_2: string | null;
    ccam_name_2: string | null;
    ccad_id_1: string | null;
    ccad_name_1: string | null;
    ccad_id_2: string | null;
    ccad_name_2: string | null;
    position_id: string | null;
    position_name: string | null;
    warehouse_name: string | null;
    warehouse_storage_space: string | null;
    sluggish_day: number;
    filepath_headshot: string | null;
}

/**
 * @description 庫存滯銷查詢 (回傳)
 * @param {number} order_create_today 今日建立訂單數
 * @param {number} order_create_growth_rate 訂單成長率
 * @param {number} order_cancel_count 取消訂單數
 * @param {number} order_return_count 退貨訂單數
 * @param {number} order_wait_to_ship_count 待出貨訂單數
 * @param {EC_StockSluggishSaleResItem[]} order_stock_sluggish_sale_list 庫存滯銷列表
 */
export interface EC_StockSluggishSaleRes {
    order_create_today: number | null;
    order_create_growth_rate: number | null;
    order_cancel_count: number | null;
    order_return_count: number | null;
    order_wait_to_ship_count: number | null;
    order_stock_sluggish_sale_list: EC_StockSluggishSaleResItem[] | null;
}

/**
 * @description 訂單收款明細 (參數)
 * @param {string} cre_date_start 建立日期起
 * @param {string} cre_date_end 建立日期訖
 * @param {number} logistics_id 物流商流水號
 * @param {string} position_id 據點流水號
 * @param {string[]} officesite_pay_statuses 線下付款狀態們
 * @param {string[]} officesite_order_statuses 線下訂單狀態們
 * @param {string[]} pay_statuses 付款狀態們
 * @param {string[]} pick_statuses 取貨狀態們
 * @param {string[]} ship_statuses 出貨狀態們
 * @param {string[]} order_statuses 訂單狀態們
 * @param {string} invoice_isopen 是否開立發票
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 * @param {string} excel 是否匯出
 */
export interface EC_OrderRevenueDetailParams {
    cre_date_start?: string;
    cre_date_end?: string;
    logistics_id?: number | null;
    position_id?: string;
    officesite_pay_statuses?: string[];
    officesite_order_statuses?: string[];
    pay_statuses?: string[];
    pick_statuses?: string[];
    ship_statuses?: string[];
    order_statuses?: string[];
    invoice_isopen?: string;
    page?: number;
    num_per_page?: number;
    excel?: string;
}

/**
 * @description 訂單收款明細 (回傳)
 * @param {string} ec_order_code 訂單編號
 * @param {string} pay_date 付款日期
 * @param {string} invoiceNo 發票號碼
 * @param {string} invoiceStatus 發票狀態
 * @param {string} customer_name 客戶名稱
 * @param {number} total_price 訂單金額
 * @param {number} invoice_price 開票金額
 * @param {number} sale_price 銷售金額（未稅）
 * @param {number} tax_price 稅額
 * @param {number} pay_price_credit 信用卡付款金額
 * @param {number} pay_price_transfer 轉帳付款金額
 * @param {number} pay_price_cash 現金付款金額
 * @param {number} pay_price_line Line Pay 付款金額
 * @param {number} pay_price_apple Apple Pay 付款金額
 * @param {number} bonus_use 使用點數
 * @param {number} bonus_get 獲得點數
 * @param {string} position_store_name 門市據點名稱
 * @param {string} Last4Number 信用卡末四碼
 */
export interface EC_OrderRevenueDetailResItem {
    ec_order_code: string | null;
    pay_date: string | null;
    invoiceNo: string | null;
    invoiceStatus: string | null;
    customer_name: string | null;
    total_price: number | null;
    invoice_price: number | null;
    sale_price: number | null;
    tax_price: number | null;
    pay_price_credit: number | null;
    pay_price_transfer: number | null;
    pay_price_cash: number | null;
    pay_price_line: number | null;
    pay_price_apple: number | null;
    bonus_use: number | null;
    bonus_get: number | null;
    position_store_name: string | null;
    Last4Number: string | null;
}

/**
 * @description 訂單發票明細 (參數)
 * @param {string} cre_date_start 建立日期起
 * @param {string} cre_date_end 建立日期訖
 * @param {string} invoiceDate_start 開票日期起
 * @param {string} invoiceDate_end 開票日期訖
 * @param {number} logistics_id 物流商流水號
 * @param {string} position_id 據點流水號
 * @param {string[]} officesite_pay_statuses 線下付款狀態們
 * @param {string[]} officesite_order_statuses 線下訂單狀態們
 * @param {string[]} pay_statuses 付款狀態們
 * @param {string[]} pick_statuses 取貨狀態們
 * @param {string[]} ship_statuses 出貨狀態們
 * @param {string[]} order_statuses 訂單狀態們
 * @param {string} invoice_isopen 是否開立發票
 * @param {string} invoiceNo 發票號碼
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 * @param {string} excel 是否匯出
 */
export interface EC_OrderInvoiceDetailParams {
    cre_date_start?: string;
    cre_date_end?: string;
    invoiceDate_start?: string;
    invoiceDate_end?: string;
    logistics_id?: number | null;
    position_id?: string;
    officesite_pay_statuses?: string[];
    officesite_order_statuses?: string[];
    pay_statuses?: string[];
    pick_statuses?: string[];
    ship_statuses?: string[];
    order_statuses?: string[];
    invoice_isopen?: string;
    invoiceNo?: string;
    page?: number;
    num_per_page?: number;
    excel?: string;
}

/**
 * @description 訂單發票明細 (回傳)
 * @param {string} invoiceNo 發票號碼
 * @param {string} invoiceDate 開票日期
 * @param {string} invoiceStatus 發票狀態
 * @param {string} unifiedRegistrationNumber 統一編號
 * @param {number} total_price 訂單金額
 * @param {number} tax_price 稅額
 * @param {number} sale_price 銷售金額（未稅）
 * @param {string} ec_order_code 訂單編號
 * @param {string} cre_date 建立日期
 * @param {string} customer_name 客戶名稱
 * @param {string} position_store_name 門市據點名稱
 * @param {string} order_message 訂單備注
 */
export interface EC_OrderInvoiceDetailResItem {
    invoiceNo: string | null;
    invoiceDate: string | null;
    invoiceStatus: string | null;
    unifiedRegistrationNumber: string | null;
    total_price: number | null;
    tax_price: number | null;
    sale_price: number | null;
    ec_order_code: string | null;
    cre_date: string | null;
    customer_name: string | null;
    position_store_name: string | null;
    order_message: string | null;
}

/**
 * @description 訂單銷售明細 (參數)
 * @param {string} cre_date_start 建立日期起
 * @param {string} cre_date_end 建立日期訖
 * @param {string} invoiceDate_start 開票日期起
 * @param {string} invoiceDate_end 開票日期訖
 * @param {number} logistics_id 物流商流水號
 * @param {string} position_id 據點流水號
 * @param {string[]} officesite_pay_statuses 線下付款狀態們
 * @param {string[]} officesite_order_statuses 線下訂單狀態們
 * @param {string[]} pay_statuses 付款狀態們
 * @param {string[]} pick_statuses 取貨狀態們
 * @param {string[]} ship_statuses 出貨狀態們
 * @param {string[]} order_statuses 訂單狀態們
 * @param {string} invoice_isopen 是否開立發票
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 * @param {string} excel 是否匯出
 */
export interface EC_OrderSaleDetailParams {
    cre_date_start?: string;
    cre_date_end?: string;
    invoiceDate_start?: string;
    invoiceDate_end?: string;
    logistics_id?: number | null;
    position_id?: string;
    officesite_pay_statuses?: string[];
    officesite_order_statuses?: string[];
    pay_statuses?: string[];
    pick_statuses?: string[];
    ship_statuses?: string[];
    order_statuses?: string[];
    invoice_isopen?: string;
    page?: number;
    num_per_page?: number;
    excel?: string;
}

/**
 * @description 訂單銷售明細 (回傳)
 * @param {string} ec_order_logistics_id 訂單物流流水號
 * @param {string} position_name 據點名稱
 * @param {string} warehouse_name 倉庫名稱
 * @param {string} warehouse_storage_space 倉位
 * @param {string} ec_order_code 訂單編號
 * @param {string} customer_name 客戶名稱
 * @param {string} cre_date 建立日期
 * @param {string} invoiceDate 開票日期
 * @param {string} invoiceNo 發票號碼
 * @param {string} commodity_code 商品代碼
 * @param {string} commodity_name 商品名稱
 * @param {string} ccad_name_1 商品分類細項1名稱
 * @param {string} ccad_name_2 商品分類細項2名稱
 * @param {number} count 數量
 * @param {number} suggested_price 定價
 * @param {number} sale_price 銷售金額
 * @param {number} sale_price_sum 銷售金額合計
 * @param {number} return_count 退貨數量
 * @param {number} refund 退款金額
 * @param {number} cost 成本
 * @param {number} cost_sum 成本合計
 * @param {number} gross_profit 毛利
 * @param {number} gross_profit_rate 毛利率
 * @param {string} ship_user_name 出貨人員
 * @param {string} order_message 訂單備注
 */
export interface EC_OrderSaleDetailResItem {
    ec_order_logistics_id: string | null;
    position_name: string | null;
    warehouse_name: string | null;
    warehouse_storage_space: string | null;
    ec_order_code: string | null;
    customer_name: string | null;
    cre_date: string | null;
    invoiceDate: string | null;
    invoiceNo: string | null;
    commodity_code: string | null;
    commodity_name: string | null;
    ccad_name_1: string | null;
    ccad_name_2: string | null;
    count: number | null;
    suggested_price: number | null;
    sale_price: number | null;
    sale_price_sum: number | null;
    return_count: number | null;
    refund: number | null;
    cost: number | null;
    cost_sum: number | null;
    gross_profit: number | null;
    gross_profit_rate: number | null;
    ship_user_name: string | null;
    order_message: string | null;
}

/**
 * @description 調撥明細報表 (參數)
 * @param {string} cre_date_start 建立日期起
 * @param {string} cre_date_end 建立日期訖
 * @param {string} date_start 調撥日期起
 * @param {string} date_end 調撥日期訖
 * @param {string} commodity_code 商品代碼
 * @param {string} commodity_name 商品名稱
 * @param {string} position_id_from 來源據點流水號
 * @param {string} warehouse_master_id_from 來源倉庫主流水號
 * @param {string} warehouse_id_from 來源倉庫流水號
 * @param {string} position_id_to 目的據點流水號
 * @param {string} warehouse_master_id_to 目的倉庫主流水號
 * @param {string} warehouse_id_to 目的倉庫流水號
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 * @param {string} excel 是否匯出
 */
export interface StockTransferDetailReportParams {
    cre_date_start?: string;
    cre_date_end?: string;
    date_start?: string;
    date_end?: string;
    commodity_code?: string;
    commodity_name?: string;
    position_id_from?: string;
    warehouse_master_id_from?: string;
    warehouse_id_from?: string;
    position_id_to?: string;
    warehouse_master_id_to?: string;
    warehouse_id_to?: string;
    page?: number;
    num_per_page?: number;
    excel?: string;
}

/**
 * @description 調撥明細報表 (回傳)
 * @param {string} stm_id 調撥單流水號
 * @param {string} date 調撥日期
 * @param {string} cre_time 建立時間
 * @param {string} name 申請人
 * @param {string} commodity_code 商品代碼
 * @param {string} commodity_name 商品名稱
 * @param {string} convert_unit_name 換算單位名稱
 * @param {number} convert_count 換算單位數量
 * @param {string} basic_unit_name 基本單位名稱
 * @param {number} basic_count 基本單位數量
 * @param {string} ccad_name_1 商品分類細項1名稱
 * @param {string} ccad_name_2 商品分類細項2名稱
 * @param {string} position_name_from 來源據點名稱
 * @param {string} warehouse_name_from 來源倉庫名稱
 * @param {string} warehouse_storage_space_from 來源倉位
 * @param {string} position_name_to 目的據點名稱
 * @param {string} warehouse_name_to 目的倉庫名稱
 * @param {string} warehouse_storage_space_to 目的倉位
 * @param {number} count 數量
 */
export interface StockTransferDetailReportResItem {
    stm_id: string | null;
    date: string | null;
    cre_time: string | null;
    name: string | null;
    commodity_code: string | null;
    commodity_name: string | null;
    convert_unit_name: string | null;
    convert_count: number;
    basic_unit_name: string | null;
    basic_count: number;
    ccad_name_1: string | null;
    ccad_name_2: string | null;
    position_name_from: string | null;
    warehouse_name_from: string | null;
    warehouse_storage_space_from: string | null;
    position_name_to: string | null;
    warehouse_name_to: string | null;
    warehouse_storage_space_to: string | null;
    count: number;
}

/**
 * @description 點名統計報表回傳
 * @param position_name 據點名稱
 * @param class_name 課級單位
 * @param user_id 員工流水號
 * @param username 員工編號
 * @param name 員工名字
 * @param count_arrive 狀態【到】次數
 * @param count_absent 狀態【未到】次數
 * @param count_leave 狀態【休假】次數
 * @param count_business 狀態【公出】次數
 * @param total_attendance_count 總出席次數
 * @param attend_rate 出席率
 */
export interface RollCallStatisticResItem {
    position_name: string | null;
    class_name: string | null;
    user_id: string | null;
    username: string | null;
    name: string | null;
    count_arrive: number | null;
    count_absent: number | null;
    count_leave: number | null;
    count_business: number | null;
    total_attendance_count: number | null;
    attend_rate: number | null;
}

//目前結構相同
export type UserRollCallStatisticParams = RollCallStatisticParams

/**
 * @description 員工點名明細回傳
 * @param rollcall_detail_id 點名細項編號
 * @param rollcall_id 點名編號
 * @param state 點名狀態
 * @param user_id 員工流水號
 * @param username 員工編號
 * @param name 員工名字
 * @param class_id 課級編號
 * @param class_name 課級單位
 * @param sc_id 班別編號
 * @param sc_name 班別名稱
 * @param last_clock_out_time 最後打卡時間
 * @param rollcall_time 點名時間
 * @param rollcall_user_id 點名人員流水號
 * @param rollcall_user_username 點名人員帳號
 * @param rollcall_user_name 點名人員姓名
 * @param position_name 據點名稱
 */
export interface UserRollCallStatisticResItem {
    rollcall_detail_id: number | null;
    rollcall_id: string | null;
    state: string | null;
    user_id: string | null;
    username: string | null;
    name: string | null;
    class_id: string | null;
    class_name: string | null;
    sc_id: string | null;
    sc_name: string | null;
    last_clock_out_time: string | null;
    rollcall_time: string | null;
    rollcall_user_id: string | null;
    rollcall_user_username: string | null;
    rollcall_user_name: string | null;
    position_name: string | null;
}

/**
 * @description 庫存使用排行報表 (參數)
 * @param start_date 日期起
 * @param end_date 日期迄
 * @param page 頁碼
 * @param num_per_page 一頁幾筆
 * @param excel 是否匯出
 */
export interface StockUsageRankingReportParams {
    start_date?: string;
    end_date?: string;
    page?: number;
    num_per_page?: number;
    excel?: string;
}
/**
 * @description 庫存使用排行報表 (回傳)
 * @param ranking 排名
 * @param rld_code 零件編號
 * @param rld_name 零件名稱
 * @param rld_count 領用數量
 * @param stock_count 庫存數量
 * @param cost 成本
 * @param sale_price 平均售價
 * @param latest_cre_time 最後領用日
 * @param car_type_names 適用車型
 */
export interface StockUsageRankingReportResItem {
    ranking: number;
    commodity_code: string;
    commodity_name: string;
    commodity_count: number;
    stock_count: number;
    cost: number;
    sale_price: number;
    latest_cre_time: string;
    car_type_names: string;
}